import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Table, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import api from '../api';

// Correzione di una fattura scartata dallo SDI: confronta cosa e' partito, l'anagrafica e il modulo
// "Dati di fatturazione" del messaggio Mirakl, poi reinvia con lo stesso numero.
const CAMPI = [
  { key: 'denominazione', label: 'Intestatario (ragione sociale)', hint: 'vuoto = nome e cognome del cliente' },
  { key: 'indirizzo', label: 'Indirizzo' },
  { key: 'numero_civico', label: 'Civico' },
  { key: 'cap', label: 'CAP' },
  { key: 'citta', label: 'Città' },
  { key: 'provincia', label: 'Provincia' },
  { key: 'partita_iva', label: 'Partita IVA' },
  { key: 'codice_fiscale', label: 'Codice fiscale' },
  { key: 'codice_destinatario', label: 'Codice destinatario' },
  { key: 'pec', label: 'PEC' },
];
const FISCALI = new Set(['denominazione', 'partita_iva', 'codice_fiscale', 'codice_destinatario', 'pec']);

const norm = (v) => (v ?? '').toString().trim().toUpperCase();

export default function CorrezioneFatturaPanel({ fatturaId, onReinviata }) {
  const [info, setInfo] = useState(null);
  const [errore, setErrore] = useState(null);
  const [dati, setDati] = useState({});
  const [aggiornaAnagrafica, setAggiornaAnagrafica] = useState(true);
  const [validazione, setValidazione] = useState({ errori: [], avvisi: [] });
  const [avvisiDaConfermare, setAvvisiDaConfermare] = useState(false);
  const [invio, setInvio] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get(`/contabilita/fatture_emesse/${fatturaId}/correzione`)
      .then(res => {
        const d = res.data;
        setInfo(d);
        // Il modulo Mirakl e' cio' che il cliente ha chiesto: ha la precedenza sull'anagrafica
        const iniziali = {};
        CAMPI.forEach(({ key }) => {
          const m = d.mirakl?.[key];
          iniziali[key] = (d.mirakl ? (m ?? (FISCALI.has(key) ? '' : d.anagrafica[key])) : d.anagrafica[key]) ?? '';
        });
        setDati(iniziali);
        setAggiornaAnagrafica(!d.intestata_a_terzi);
      })
      .catch(e => setErrore(e.response?.data?.detail || 'Impossibile caricare i dati della fattura.'));
  }, [fatturaId]);

  useEffect(() => {
    if (!info) return;
    clearTimeout(timer.current);
    setAvvisiDaConfermare(false);
    timer.current = setTimeout(() => {
      api.post(`/contabilita/fatture_emesse/${fatturaId}/valida-destinatario`, dati)
        .then(res => setValidazione(res.data))
        .catch(() => {});
    }, 400);
    return () => clearTimeout(timer.current);
  }, [dati, info, fatturaId]);

  const campiErrore = useMemo(() => new Set(info?.campi_errore || []), [info]);

  if (errore) return <Alert variant="warning" className="small">{typeof errore === 'string' ? errore : 'Errore'}</Alert>;
  if (!info) return <div className="text-center py-2"><Spinner size="sm" animation="border" /></div>;

  const usaMirakl = () => {
    const nuovi = { ...dati };
    CAMPI.forEach(({ key }) => { nuovi[key] = info.mirakl[key] ?? (FISCALI.has(key) ? '' : nuovi[key]); });
    setDati(nuovi);
  };

  const reinvia = async (forza) => {
    setInvio(true);
    setErrore(null);
    try {
      const res = await api.post(`/contabilita/fatture_emesse/${fatturaId}/correggi-reinvia`, {
        dati, aggiorna_anagrafica: aggiornaAnagrafica, forza,
      });
      onReinviata(res.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.avvisi) {
        setValidazione(d);
        setAvvisiDaConfermare(true);
      } else if (d?.errori) {
        setValidazione(d);
      } else {
        setErrore(typeof d === 'string' ? d : 'Reinvio non riuscito.');
      }
    } finally {
      setInvio(false);
    }
  };

  const cella = (v) => v ? v : <span className="text-muted">—</span>;
  const diverso = (key) => info.mirakl && norm(info.mirakl[key]) !== norm(dati[key]);

  return (
    <div className="border rounded p-2 mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>Correggi e reinvia</strong>
        {info.mirakl
          ? <Button size="sm" variant="outline-primary" onClick={usaMirakl}>Usa tutti i dati del messaggio Mirakl</Button>
          : <small className="text-muted">Nessun modulo "Dati di fatturazione" nei messaggi Mirakl dell'ordine</small>}
      </div>
      {info.intestata_a_terzi && (
        <Alert variant="info" className="py-1 px-2 small mb-2">
          Il cliente ha chiesto la fattura intestata a <strong>{info.mirakl.denominazione}</strong>, diverso
          da {info.cliente_nome || 'lui'}: i dati valgono solo per questa fattura, la scheda cliente non viene toccata.
        </Alert>
      )}
      <Table size="sm" bordered className="small mb-2 align-middle">
        <thead>
          <tr>
            <th style={{ width: '20%' }}>Campo</th>
            <th style={{ width: '22%' }}>Inviato in fattura</th>
            <th style={{ width: '22%' }}>Messaggio Mirakl</th>
            <th>Da usare</th>
          </tr>
        </thead>
        <tbody>
          {CAMPI.map(({ key, label, hint }) => (
            <tr key={key} className={campiErrore.has(key) ? 'table-danger' : undefined}>
              <td>
                {label}
                {campiErrore.has(key) && <Badge bg="danger" className="ms-1">scarto</Badge>}
              </td>
              <td>{cella(info.inviato?.[key])}</td>
              <td>{info.mirakl ? cella(info.mirakl[key]) : <span className="text-muted">—</span>}</td>
              <td>
                <Form.Control size="sm" value={dati[key] ?? ''} placeholder={hint}
                  className={diverso(key) ? 'border-warning' : undefined}
                  onChange={e => setDati({ ...dati, [key]: e.target.value })} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="small text-muted mb-2">
        L'indirizzo vale solo per la fattura: in anagrafica resta quello di spedizione.
        {info.inviato?.indirizzo && ' In "Inviato" il civico è compreso nell\'indirizzo.'}
      </div>
      <Form.Check type="checkbox" id={`aggiorna-anagrafica-${fatturaId}`} className="small mb-2"
        checked={aggiornaAnagrafica} onChange={e => setAggiornaAnagrafica(e.target.checked)}
        label="Salva ragione sociale, P.IVA, codice fiscale, codice destinatario e PEC nella scheda cliente" />

      {validazione.errori?.length > 0 && (
        <Alert variant="danger" className="py-1 px-2 small mb-2">
          <ul className="mb-0 ps-3">{validazione.errori.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </Alert>
      )}
      {validazione.avvisi?.length > 0 && (
        <Alert variant="warning" className="py-1 px-2 small mb-2">
          <ul className="mb-0 ps-3">{validazione.avvisi.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </Alert>
      )}
      {info.messaggio_cliente_pendente && (
        <div className="small text-muted mb-2">La risposta al cliente su IBS partirà quando lo SDI confermerà la consegna.</div>
      )}
      <div className="text-end">
        <Button variant={avvisiDaConfermare ? 'danger' : 'warning'} disabled={invio || validazione.errori?.length > 0}
          onClick={() => reinvia(avvisiDaConfermare)}>
          {invio ? <Spinner size="sm" animation="border" /> : avvisiDaConfermare ? 'Reinvia comunque' : 'Salva e reinvia'}
        </Button>
      </div>
    </div>
  );
}
